<!-- Purchase Plan Component -->
<div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
  <!-- Header -->
  <div class="bg-white shadow-sm border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <div class="flex items-center space-x-4">
          <app-back-button
            label="Back to Dashboard"
            link="/home/dashboard">
          </app-back-button>
          <h1 class="text-2xl font-bold text-gray-900">Purchase Plan</h1>
        </div>
        
        <div class="flex items-center space-x-4">
          <img 
            *ngIf="headerLogo" 
            [src]="headerLogo" 
            alt="SigmaT" 
            class="h-10 w-auto object-contain"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div class="flex flex-col lg:flex-row gap-6 p-2 overflow-auto">
      <!-- Left Column: Purchase Plan Form -->
      <div class="flex flex-col bg-white rounded-2xl shadow-md p-6 space-y-8 flex-1">
        <div class="text-center">
          <h2 class="text-3xl font-bold text-gray-800 mb-2">
            Choose Your Subscription Plan
          </h2>
          <p class="text-gray-600">
            Select the domains and configure your requirements to get a personalized pricing
          </p>
        </div>

        <!-- Domain Selection -->
        <section class="mb-6">
          <label class="block font-semibold text-lg mb-4 text-gray-700">
            Choose Your Domains
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              *ngFor="let domain of domains"
              class="cursor-pointer border rounded-xl p-6 text-center transition-all bg-gray-50 hover:bg-blue-50 shadow-sm hover:shadow-md"
              [ngClass]="{
                'ring-2 ring-blue-500 bg-blue-100 shadow-md border-blue-300':
                  selectedDomains.includes(domain.name)
              }"
              (click)="
                onDomainChange({
                  target: {
                    value: domain.name,
                    checked: !selectedDomains.includes(domain.name)
                  }
                })
              "
            >
              <div class="mb-3">
                <div class="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                </div>
              </div>
              <h3 class="font-semibold text-gray-800 mb-2">{{ domain.name }}</h3>
              <p class="text-sm text-gray-600 mb-2">{{ domain.description }}</p>
              <div class="text-lg font-bold text-blue-600">
                ₹{{ domain.price }} {{ domain.unit }}
              </div>
            </div>
          </div>
        </section>

        <!-- Purchase Plan Form -->
        <form
          [formGroup]="form"
          (ngSubmit)="calculateTotal()"
          class="space-y-6 overflow-auto"
        >
          <!-- General Info -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block mb-2 text-sm font-semibold text-gray-700">
                Organization ID
              </label>
              <input
                formControlName="organization_id"
                type="text"
                placeholder="Enter Organization ID"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors duration-200"
              />
            </div>

            <div>
              <label
                for="duration"
                class="block mb-2 text-sm font-semibold text-gray-700"
              >
                Duration (months)
              </label>
              <select
                formControlName="duration"
                id="duration"
                (change)="calculateTotal()"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors duration-200"
              >
                <option *ngFor="let month of monthOptions" [value]="month">
                  {{ month }} month{{ month > 1 ? "s" : "" }}
                </option>
              </select>
            </div>
          </div>

          <!-- Dynamic Domain Sections -->
          <ng-container
            *ngFor="
              let domain of [
                'Consultancy',
                'Road',
                'Construction',
                'Utilities'
              ]
            "
          >
            <div
              *ngIf="selectedDomains.includes(domain)"
              [formGroupName]="domain"
              class="mt-6 p-6 border rounded-xl bg-gray-50 shadow-sm"
            >
              <h3 class="text-lg font-bold text-blue-700 mb-4">
                {{ domain }} Configuration
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ng-container [ngSwitch]="domain">
                  <ng-container *ngSwitchCase="'Consultancy'">
                    <div>
                      <label class="block mb-2 text-sm font-medium text-gray-700">
                        Projects per Month
                      </label>
                      <input
                        formControlName="projects"
                        type="number"
                        min="0"
                        placeholder="Projects/month"
                        (input)="calculateTotal()"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label class="block mb-2 text-sm font-medium text-gray-700">
                        Users
                      </label>
                      <input
                        formControlName="users_count"
                        type="number"
                        min="0"
                        placeholder="Number of users"
                        (input)="calculateTotal()"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors duration-200"
                      />
                    </div>
                  </ng-container>
                  
                  <ng-container *ngSwitchDefault>
                    <div>
                      <label class="block mb-2 text-sm font-medium text-gray-700">
                        Users
                      </label>
                      <input
                        formControlName="users_count"
                        type="number"
                        min="0"
                        placeholder="Number of users"
                        (input)="calculateTotal()"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors duration-200"
                      />
                    </div>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </ng-container>

          <!-- Action Buttons -->
          <div class="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              (click)="goBack()"
              class="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              (click)="proceedToPayment()"
              class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 font-semibold"
            >
              Proceed to Payment
            </button>
          </div>
        </form>
      </div>

      <!-- Right Column: Pricing Summary -->
      <div class="w-full lg:w-96">
        <div class="bg-white rounded-2xl shadow-md p-6 sticky top-6">
          <h3 class="text-xl font-bold text-gray-800 mb-6">Pricing Summary</h3>
          
          <div class="space-y-4">
            <div *ngFor="let domain of selectedDomains" class="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <span class="font-medium text-gray-700">{{ domain }}</span>
                <div class="text-sm text-gray-500">
                  ₹{{ getDomainPrice(domain) }} {{ getDomainUnit(domain) }}
                </div>
              </div>
              <span class="font-semibold text-gray-800">
                ₹{{ getDomainPrice(domain) * (form.get(domain)?.get('users_count')?.value || 0) }}
              </span>
            </div>
            
            <div class="pt-4 border-t border-gray-200">
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-600">Duration:</span>
                <span class="font-medium">{{ form.get('duration')?.value }} months</span>
              </div>
              <div class="flex justify-between items-center text-lg font-bold text-gray-800">
                <span>Total Amount:</span>
                <span class="text-blue-600">₹{{ totalAmount }}</span>
              </div>
            </div>
          </div>

          <!-- Support Information -->
          <div class="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 class="font-semibold text-blue-800 mb-2">Need Help?</h4>
            <div class="text-sm text-blue-700 space-y-1">
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                {{ supportContactEmail }}
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                {{ supportContactNumber }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
